// ===== INTERFACES PRINCIPAIS =====

export interface VehicleRental {
  // ===== INFORMAÇÕES BÁSICAS =====
  id: number;
  rentalNumber: string;
  startDate: Date;
  endDate: Date;
  actualStartDate?: Date;
  actualEndDate?: Date;
  status: string;
  priority: string;

  // ===== CLIENTE =====
  customerId: number;
  customerName?: string;
  customerDocument?: string;
  customerEmail?: string;
  customerPhone?: string;

  // ===== MOTORISTA =====
  driverName?: string;
  driverDocument?: string;
  driverLicenseNumber?: string;
  driverLicenseExpiryDate?: Date;
  driverLicenseCategory?: string;

  // ===== VALORES FINANCEIROS =====
  paymentStatus?: string;
  paymentMethod?: string;

  // ===== QUILOMETRAGEM =====
  startMileage?: number;
  endMileage?: number;
  allowedMileage?: number;
  excessMileage?: number;
  excessMileageRate?: number;
  excessMileageCharge?: number;

  // ===== COMBUSTÍVEL =====
  fuelLevelAtStart?: string;
  fuelLevelAtEnd?: string;
  fuelCharge?: number;

  // ===== LOCALIZAÇÃO =====
  pickupLocation?: string;
  returnLocation?: string;
  pickupAddress?: string;
  returnAddress?: string;

  // ===== FUNCIONÁRIOS =====
  createdByStaffId?: number;
  assignedStaffId?: number;
  staffName?: string;

  // ===== OBSERVAÇÕES =====
  notes?: string;
  internalNotes?: string;
  cancellationReason?: string;

  // ===== SEGURO =====
  insuranceIncluded: boolean;
  insuranceCost?: number;
  insuranceProvider?: string;
  insurancePolicyNumber?: string;

  // ===== NAVEGAÇÃO =====
  rentalItems: VehicleRentalItem[];
  rentalHistory: VehicleRentalHistory[];
  attachments: VehicleRentalAttachment[];

  // ===== CAMPOS CALCULADOS =====
  daysToStart: number;
  daysToEnd: number;
  isOverdue: boolean;
  isActive: boolean;
  canBeStarted: boolean;
  canBeEnded: boolean;
  canBeCancelled: boolean;

  // ===== CAMPOS BASE =====
  updatedAt?: Date;
  createdAt: Date;
}

export interface VehicleRentalItem {
  // ===== RELACIONAMENTOS =====
  id: number;
  vehicleRentalId: number;
  vehicleId: number;

  // ===== DADOS DO VEÍCULO =====
  plateNumber: string;
  brand: string;
  model: string;
  modelYear: number;
  color?: string;
  fuelType?: string;
  transmissionType?: string;
  engineCapacity?: number;
  doors?: number;
  vehicleCategory?: string;

  // ===== QUILOMETRAGEM =====
  startMileage?: number;
  endMileage?: number;
  allowedMileage?: number;
  excessMileage?: number;
  excessMileageRate?: number;
  excessMileageCharge?: number;

  // ===== COMBUSTÍVEL =====
  fuelLevelAtStart?: string;
  fuelLevelAtEnd?: string;
  fuelCharge?: number;

  

  // ===== STATUS =====
  status: string;
  actualStartDate?: Date;
  actualEndDate?: Date;

  // ===== CONDIÇÕES =====
  conditionAtStart?: string;
  conditionAtEnd?: string;
  hasDamage: boolean;
  damageDescription?: string;
  damageCost?: number;

  // ===== ACESSÓRIOS =====
  accessoriesAtStart?: string;
  accessoriesAtEnd?: string;
  missingAccessories: boolean;
  missingAccessoriesDescription?: string;
  missingAccessoriesCost?: number;

  // ===== OBSERVAÇÕES =====
  notes?: string;
  internalNotes?: string;

  // ===== CAMPOS CALCULADOS =====
  remainingDays: number;
  isOverdue: boolean;
  isActive: boolean;
  canBeStarted: boolean;
  canBeEnded: boolean;

  // ===== CAMPOS BASE =====
  updatedAt?: Date;
  createdAt: Date;
}

export interface VehicleRentalHistory {
  // ===== RELACIONAMENTOS =====
  id: number;
  vehicleRentalId: number;

  // ===== AÇÃO =====
  action: string;
  status: string;
  previousStatus?: string;

  // ===== RESPONSÁVEL =====
  staffId?: number;
  staffName?: string;
  userName?: string;

  // ===== DATA =====
  actionDate: Date;
  actionTime?: string;

  // ===== LOCALIZAÇÃO =====
  location?: string;
  ipAddress?: string;
  userAgent?: string;

  // ===== OBSERVAÇÕES =====
  description?: string;
  notes?: string;
  internalNotes?: string;

  // ===== VALORES =====
  amount?: number;
  paymentMethod?: string;
  transactionId?: string;

  // ===== QUILOMETRAGEM =====
  mileage?: number;
  mileageDifference?: number;

  // ===== COMBUSTÍVEL =====
  fuelLevel?: string;

  // ===== CONDIÇÕES =====
  vehicleCondition?: string;
  hasDamage?: boolean;
  damageDescription?: string;

  // ===== ACESSÓRIOS =====
  accessories?: string;
  missingAccessories?: boolean;

  // ===== MOTIVOS =====
  reason?: string;
  cancellationReason?: string;

  // ===== APROVAÇÃO =====
  requiresApproval: boolean;
  isApproved: boolean;
  approvedByStaffId?: number;
  approvedByStaffName?: string;
  approvalDate?: Date;
  approvalNotes?: string;

  // ===== NOTIFICAÇÃO =====
  notificationSent: boolean;
  notificationSentDate?: Date;
  notificationRecipients?: string;

  // ===== CAMPOS BASE =====
  updatedAt?: Date;
  createdAt: Date;
}

export interface VehicleRentalAttachment {
  // ===== RELACIONAMENTOS =====
  id: number;
  vehicleRentalId: number;

  // ===== ARQUIVO =====
  fileName: string;
  filePath: string;
  fileType?: string;
  fileExtension?: string;
  fileSize?: number;
  mimeType?: string;

  // ===== CATEGORIA =====
  category: string;
  subCategory?: string;
  description?: string;

  // ===== STATUS =====
  status: string;
  isRequired: boolean;
  isPublic: boolean;

  // ===== UPLOAD =====
  uploadedBy?: string;
  uploadDate?: Date;
  originalFileName?: string;

  // ===== VERSÃO =====
  version: number;
  isLatestVersion: boolean;
  previousVersionId?: number;

  // ===== SEGURANÇA =====
  hash?: string;
  checksum?: string;
  isEncrypted: boolean;
  encryptionKey?: string;

  // ===== ACESSO =====
  accessCount?: number;
  lastAccessedDate?: Date;
  lastAccessedBy?: string;

  // ===== EXPIRAÇÃO =====
  expirationDate?: Date;
  isExpired: boolean;

  // ===== BACKUP =====
  isBackedUp: boolean;
  backupDate?: Date;
  backupLocation?: string;

  // ===== OBSERVAÇÕES =====
  notes?: string;
  internalNotes?: string;
  tags?: string;

  // ===== CAMPOS BASE =====
  updatedAt?: Date;
  createdAt: Date;
}

// ===== ENUMS =====

export enum VehicleRentalStatus {
  PENDENTE = 'Pendente',
  ATIVA = 'Ativa',
  FINALIZADA = 'Finalizada',
  CANCELADA = 'Cancelada'
}

export enum VehicleRentalItemStatus {
  PENDENTE = 'Pendente',
  ATIVO = 'Ativo',
  FINALIZADO = 'Finalizado',
  CANCELADO = 'Cancelado'
}

export enum VehicleRentalPriority {
  BAIXA = 'Baixa',
  NORMAL = 'Normal',
  ALTA = 'Alta',
  URGENTE = 'Urgente'
}

export enum FuelLevel {
  VAZIO = 'Vazio',
  QUARTO = '1/4',
  METADE = '1/2',
  TRES_QUARTOS = '3/4',
  CHEIO = 'Cheio'
}

export enum LicenseCategory {
  A = 'A',
  B = 'B',
  C = 'C',
  D = 'D',
  E = 'E'
}

// ===== DTOs DE REQUISIÇÃO =====

export interface CreateVehicleRentalRequest {
  rentalNumber?: string;
  startDate: Date;
  endDate: Date;
  priority: string;
  customerId: number;
  driverName?: string;
  driverDocument?: string;
  driverLicenseNumber?: string;
  driverLicenseExpiryDate?: Date;
  driverLicenseCategory?: string;
  // Removed rental financial inputs per new API
  allowedMileage?: number;
  excessMileageRate?: number;
  fuelLevelAtStart?: string;
  pickupLocation?: string;
  returnLocation?: string;
  pickupAddress?: string;
  returnAddress?: string;
  assignedStaffId?: number;
  notes?: string;
  internalNotes?: string;
  insuranceIncluded: boolean;
  insuranceProvider?: string;
  insurancePolicyNumber?: string;
  rentalItems: CreateVehicleRentalItemRequest[];
}

export interface CreateVehicleRentalItemRequest {
  vehicleId: number;
  startMileage?: number;
  conditionAtStart?: string;
  accessoriesAtStart?: string;
  fuelLevelAtStart?: string;
  notes?: string;
}

export interface UpdateVehicleRentalRequest {
  rentalNumber?: string;
  startDate?: Date;
  endDate?: Date;
  priority?: string;
  customerId?: number;
  driverName?: string;
  driverDocument?: string;
  driverLicenseNumber?: string;
  driverLicenseExpiryDate?: Date;
  driverLicenseCategory?: string;
  // Removed rental financial inputs per new API
  allowedMileage?: number;
  excessMileageRate?: number;
  fuelLevelAtStart?: string;
  pickupLocation?: string;
  returnLocation?: string;
  pickupAddress?: string;
  returnAddress?: string;
  assignedStaffId?: number;
  notes?: string;
  internalNotes?: string;
  insuranceIncluded?: boolean;
  insuranceProvider?: string;
  insurancePolicyNumber?: string;
}

export interface StartRentalRequest {
  actualStartDate?: Date;
  startMileage?: number;
  conditionAtStart?: string;
  fuelLevelAtStart?: string;
  accessoriesAtStart?: string;
  notes?: string;
}

export interface EndRentalRequest {
  actualEndDate?: Date;
  endMileage?: number;
  conditionAtEnd?: string;
  fuelLevelAtEnd?: string;
  accessoriesAtEnd?: string;
  hasDamage?: boolean;
  damageDescription?: string;
  missingAccessories?: boolean;
  missingAccessoriesDescription?: string;
  notes?: string;
}

export interface CancelRentalRequest {
  reason: string;
  notes?: string;
}

export interface VehicleRentalFilter {
  status?: string;
  customerId?: number;
  startDate?: Date;
  endDate?: Date;
  assignedStaffId?: number;
  rentalNumber?: string;
  plateNumber?: string;
}

export interface VehicleRentalStatistics {
  totalRentals: number;
  activeRentals: number;
  pendingRentals: number;
  completedRentals: number;
  cancelledRentals: number;
  overdueRentals: number;
  // Removed totalRevenue per new contract
  averageRentalDays: number;
  mostRentedVehicleId?: number;
  mostRentedVehicleName?: string;
}

// ===== HISTÓRICO =====
export interface VehicleRentalHistoryDTO {
  // ===== RELACIONAMENTOS =====
  id?: number;
  vehicleRentalId: number;

  // ===== AÇÃO =====
  action: string;
  status: string;
  previousStatus?: string;

  // ===== RESPONSÁVEL =====
  staffId?: number;
  staffName?: string;
  userName?: string;

  // ===== DATA =====
  actionDate: Date;
  actionTime?: string;

  // ===== LOCALIZAÇÃO =====
  location?: string;
  ipAddress?: string;
  userAgent?: string;

  // ===== OBSERVAÇÕES =====
  description?: string;
  notes?: string;
  internalNotes?: string;

  // ===== VALORES =====
  amount?: number;
  paymentMethod?: string;
  transactionId?: string;

  // ===== QUILOMETRAGEM =====
  mileage?: number;
  mileageDifference?: number;

  // ===== COMBUSTÍVEL =====
  fuelLevel?: string;

  // ===== CONDIÇÕES =====
  vehicleCondition?: string;
  hasDamage?: boolean;
  damageDescription?: string;

  // ===== ACESSÓRIOS =====
  accessories?: string;
  missingAccessories?: boolean;
}

export interface CreateVehicleRentalHistoryRequest {
  vehicleRentalId: number;
  action: string;
  status: string;
  previousStatus?: string;
  staffId?: number;
  userName?: string;
  description?: string;
  notes?: string;
}

export interface UpdateVehicleRentalHistoryRequest {
  id: number;
  vehicleRentalId: number;
  action: string;
  status: string;
  previousStatus?: string;
  staffId?: number;
  userName?: string;
  description?: string;
  notes?: string;
}

export interface LogVehicleRentalActionRequest {
  vehicleRentalId: number;
  action: string;
  status: string;
  description?: string;
  userName?: string;
  staffId?: number;
}

export interface LogVehicleRentalStatusChangeRequest {
  vehicleRentalId: number;
  fromStatus: string;
  toStatus: string;
  reason?: string;
  userName?: string;
  staffId?: number;
}

// ===== ANEXOS =====
export interface VehicleRentalAttachmentDTO {
  // ===== RELACIONAMENTOS =====
  id?: number;
  vehicleRentalId: number;

  // ===== ARQUIVO =====
  fileName: string;
  filePath: string;
  fileSize?: number;
  mimeType?: string;
  fileType?: string;
  fileExtension?: string;

  // ===== UPLOAD =====
  uploadedBy?: string;
  uploadDate: Date;
  lastModifiedDate?: Date;

  // ===== CATEGORIA =====
  category: string;
  subCategory?: string;
  tags?: string;

  // ===== DESCRIÇÃO =====
  description?: string;
  notes?: string;

  // ===== STATUS =====
  status: string;
  isActive: boolean;
  isPublic: boolean;

  // ===== VERSÃO =====
  version: number;
  isLatestVersion: boolean;
  originalAttachmentId?: number;

  // ===== SEGURANÇA =====
  isEncrypted: boolean;
  encryptionKey?: string;
  hash?: string;
  checksum?: string;

  // ===== EXPIRAÇÃO =====
  expirationDate?: Date;

  // ===== BACKUP =====
  isBackedUp: boolean;
  backupLocation?: string;
  backupDate?: Date;

  // ===== ACESSO =====
  downloadCount: number;
  lastAccessedDate?: Date;
  lastAccessedBy?: string;

  // ===== METADADOS =====
  createdAt: Date;
  createdBy?: number;
  updatedAt?: Date;
  updatedBy?: number;
}

export interface CreateVehicleRentalAttachmentRequest {
  vehicleRentalId: number;
  fileName: string;
  filePath: string;
  category: string;
  uploadedBy?: string;
  description?: string;
  notes?: string;
  tags?: string;
}

export interface UpdateVehicleRentalAttachmentRequest {
  id: number;
  vehicleRentalId: number;
  fileName: string;
  filePath: string;
  category: string;
  description?: string;
  notes?: string;
  tags?: string;
  status: string;
}

export interface UploadFileRequest {
  vehicleRentalId: number;
  file: File;
  category: string;
  description?: string;
  tags?: string;
}

export interface AttachmentCategory {
  value: string;
  label: string;
  description: string;
  required: boolean;
  maxFiles?: number;
  allowedExtensions?: string[];
}